import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ssoApi from "../../api/sso/ssoApi";
import { completeSsoLogin, INTERNAL_ONLY_MESSAGE, SSO_CALLBACK_URL } from "../../auth/sso";
import stytchClient from "../../services/stytchClient";
import colors from "../../designSystem/colors";
import { Spinner } from "../../components";
import MfaStep, { MfaChallenge } from "../../components/MfaStep";

const { UI } = colors;

const spinKeyframes = `
@keyframes spin {
  to { transform: rotate(360deg); }
}
`;

const InlineSpinner: React.FC = () => (
  <div
    style={{
      width: 16,
      height: 16,
      border: `2px solid ${UI.BORDER.STANDARD}`,
      borderTopColor: UI.TEXT.ACTIVE,
      borderRadius: "50%",
      animation: "spin 0.8s linear infinite",
      display: "inline-block",
      boxSizing: "border-box",
      flexShrink: 0,
    }}
  />
);

const SSO_ERROR_MESSAGES: Record<string, string> = {
  no_organizations: "No organization found for your account. Please contact your administrator.",
  agency_not_found:
    "Your organization is not linked to Coverflow. Please contact your administrator.",
  auth_method_not_allowed:
    "Google login is not enabled for your organization. Please use an allowed sign-in method.",
  member_not_authenticated:
    "Your organization requires an authentication method not supported by this login. Please contact your administrator.",
  invalid_token: "SSO session expired. Please try logging in again.",
  intermediate_session_not_found: "Login session expired. Please try logging in again.",
};

interface SSOOrganization {
  organization_id: string;
  organization_name: string;
  membership_type?: string;
  requires_sso?: boolean;
  sso_connection_id?: string | null;
  method_not_allowed?: boolean;
  allowed_methods?: string[];
}

const pendingTokenRequests = new Map<string, Promise<any>>();

const SSOCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  // Set when user tries OAuth but the organization requires corporate SSO (SAML/OIDC)
  const [ssoRequired, setSsoRequired] = useState<{
    connectionId: string | null;
    orgName: string;
  } | null>(null);

  // Organization selection state when user belongs to multiple organizations
  const [organizations, setOrganizations] = useState<SSOOrganization[] | null>(null);
  const [intermediateToken, setIntermediateToken] = useState<string | null>(null);
  const [loadingSelection, setLoadingSelection] = useState<string | null>(null);
  const [hasStartedExchange, setHasStartedExchange] = useState(false);

  const [mfa, setMfa] = useState<MfaChallenge | null>(null);

  // Auto-redirect to /login after 3 seconds when an error occurs
  useEffect(() => {
    if (!error) {
      setCountdown(null);
      return;
    }

    setCountdown(3);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          navigate("/login", { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [error, navigate]);

  const handleAuthSuccess = (data: any) => {
    if (!completeSsoLogin(data)) {
      setError(INTERNAL_ONLY_MESSAGE);
      setMfa(null); // the MFA screen renders before the error screen
      setOrganizations(null);
      setIntermediateToken(null);
      return;
    }
    navigate("/", { replace: true });
  };

  const handleMfaRequired = (data: any) => {
    setMfa({
      memberId: data.member_id,
      organizationId: data.organization_id,
      intermediateToken: data.intermediate_session_token,
      email: data.email || undefined,
      needsEnrollment: !data.totp_registration_id,
    });
  };

  const startSsoRedirect = (connectionId: string, orgName: string) => {
    if (!stytchClient) {
      setError(`"${orgName}" requires SSO login but SSO is not configured in this environment.`);
      setOrganizations(null);
      setIntermediateToken(null);
      return;
    }
    try {
      stytchClient.sso.start({
        connection_id: connectionId,
        login_redirect_url: SSO_CALLBACK_URL,
        signup_redirect_url: SSO_CALLBACK_URL,
      });
    } catch (err: any) {
      setError(`Could not start SSO for "${orgName}". Please contact your administrator.`);
      setOrganizations(null);
      setIntermediateToken(null);
    }
  };

  const handleSelectOrg = async (orgId: string) => {
    if (hasStartedExchange) return;

    const org = organizations?.find((o) => o.organization_id === orgId);
    if (org?.requires_sso && org.sso_connection_id) {
      startSsoRedirect(org.sso_connection_id, org.organization_name);
      return;
    }

    if (org?.method_not_allowed) {
      const allowed = org.allowed_methods?.length
        ? ` Allowed methods: ${org.allowed_methods.join(', ')}.`
        : '';
      setError(
        `Sign-in method is not enabled for "${org.organization_name}". Please use an allowed method.${allowed}`
      );
      setOrganizations(null);
      setIntermediateToken(null);
      return;
    }

    if (!intermediateToken) {
      setError("Session expired. Please try logging in again.");
      setOrganizations(null);
      return;
    }

    setHasStartedExchange(true);
    setLoadingSelection(orgId);

    try {
      const exchangeResp = await ssoApi.discoveryExchange(intermediateToken, orgId);
      if (!exchangeResp || exchangeResp.status !== 200) {
        const errKey = (exchangeResp?.body as any)?.error;
        const exchangeErr =
          SSO_ERROR_MESSAGES[errKey] ||
          (exchangeResp?.body as any)?.detail ||
          "Unable to complete sign-in. Please try again.";
        // Reset organization list and show error screen so user cannot click another org with a spent token
        setOrganizations(null);
        setIntermediateToken(null);
        setError(exchangeErr);
        setHasStartedExchange(false);
        return;
      }

      const userData: any = exchangeResp.body;
      if (userData.action === "mfa_required") {
        handleMfaRequired(userData);
      } else {
        handleAuthSuccess(userData);
      }
    } catch {
      setOrganizations(null);
      setIntermediateToken(null);
      setError("Network error during organization selection. Please try again.");
      setHasStartedExchange(false);
    } finally {
      setLoadingSelection(null);
    }
  };

  useEffect(() => {
    // In-app flows (OTP) pass the token via router state so it never touches the
    // URL; Stytch redirects bring it as query params.
    const navState = location.state as { token?: string; tokenType?: string } | null;
    const params = new URLSearchParams(window.location.search);
    const token = navState?.token || params.get("token");
    const rawTokenType = navState?.tokenType || params.get("stytch_token_type") || "discovery";

    if (!token) {
      setError("No token found in the callback URL.");
      return;
    }

    let tokenType = rawTokenType;
    const oauthProvider = localStorage.getItem("pending_oauth_provider");
    if (oauthProvider) {
      localStorage.removeItem("pending_oauth_provider");
    }
    if (rawTokenType === "discovery_oauth") {
      tokenType = oauthProvider ? `oauth_${oauthProvider}` : "discovery_oauth";
    }

    // Clean query parameters from URL to avoid re-triggering on manual refresh
    window.history.replaceState({}, document.title, window.location.pathname);

    const existingPromise = pendingTokenRequests.get(token);
    const promise =
      existingPromise ||
      (async () => {
        try {
          const resp = await ssoApi.stytchCallback(token, tokenType);
          if (resp.status !== 200) {
            const errKey = (resp.body as any)?.error;
            const errMsg =
              SSO_ERROR_MESSAGES[errKey] ||
              (resp.body as any)?.detail ||
              "Unable to complete sign-in. Please try again.";
            return { ok: false, error: errMsg };
          }

          const data: any = resp.body;

          if (data.action === "redirect_to_sso") {
            return {
              ok: true,
              ssoRequiredData: {
                connectionId: data.connection_id || null,
                orgName: data.organization_name || "your organization",
              },
            };
          }

          if (data.action === "mfa_required") {
            return { ok: true, mfaData: data };
          }

          if (
            data.action === "select_organization" ||
            (data.organizations && data.intermediate_token)
          ) {
            if (!data.organizations || data.organizations.length === 0) {
              return {
                ok: false,
                error: SSO_ERROR_MESSAGES.no_organizations,
              };
            }
            return {
              ok: true,
              selectOrgData: {
                organizations: data.organizations,
                intermediateToken: data.intermediate_token,
              },
            };
          }

          return { ok: true, userData: data };
        } catch (err) {
          console.error("SSO callback error", err);
          return { ok: false, error: "Network error during sign-in." };
        }
      })();

    if (!existingPromise) {
      pendingTokenRequests.set(token, promise);
    }

    promise.then((result) => {
      if (!result.ok) {
        setError(result.error);
        setOrganizations(null);
        setIntermediateToken(null);
      } else if (result.ssoRequiredData) {
        setSsoRequired(result.ssoRequiredData);
      } else if (result.mfaData) {
        handleMfaRequired(result.mfaData);
      } else if (result.selectOrgData) {
        if (
          !result.selectOrgData.organizations ||
          result.selectOrgData.organizations.length === 0
        ) {
          setError(SSO_ERROR_MESSAGES.no_organizations);
          setOrganizations(null);
          setIntermediateToken(null);
        } else {
          setOrganizations(result.selectOrgData.organizations);
          setIntermediateToken(result.selectOrgData.intermediateToken);
        }
      } else if (result.userData) {
        handleAuthSuccess(result.userData);
      }
    });
  }, [navigate, location.state]);

  // Single-org fast path: automatically select if only 1 organization and not already handled
  useEffect(() => {
    if (
      organizations?.length === 1 &&
      intermediateToken &&
      !loadingSelection &&
      !error &&
      !hasStartedExchange &&
      !ssoRequired
    ) {
      handleSelectOrg(organizations[0].organization_id);
    }
  }, [organizations, error, loadingSelection, hasStartedExchange, intermediateToken, ssoRequired]);

  // Screen 1: SSO required (user attempted OAuth but organization requires SAML/OIDC SSO)
  if (ssoRequired) {
    const canStartSso = !!(ssoRequired.connectionId && stytchClient);
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          fontFamily: "Assistant, sans-serif",
          background: UI.BACKGROUND.STANDARD,
          padding: 20,
        }}
      >
        <style>{spinKeyframes}</style>
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            background: "#fff",
            padding: 32,
            borderRadius: 12,
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            textAlign: "center",
          }}
        >
          <h2 style={{ color: UI.TEXT.ACTIVE, marginBottom: 8, fontSize: 22 }}>
            Single Sign-On Required
          </h2>
          <p
            style={{
              color: UI.TEXT.GREY,
              marginBottom: 24,
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            Google login is not enabled for <strong>{ssoRequired.orgName}</strong>.
            {canStartSso
              ? " Please continue with your organization's Single Sign-On (SSO)."
              : " No SSO connection is configured yet — please contact your administrator."}
          </p>

          {canStartSso && (
            <button
              onClick={() => startSsoRedirect(ssoRequired.connectionId!, ssoRequired.orgName)}
              style={{
                width: "100%",
                padding: "12px 16px",
                marginBottom: 16,
                borderRadius: 8,
                border: "none",
                backgroundColor: UI.TEXT.ACTIVE,
                color: "#fff",
                cursor: "pointer",
                fontSize: 15,
                fontWeight: 600,
              }}
            >
              🔑 Continue with SSO
            </button>
          )}

          <div>
            <a
              href="/login"
              style={{ color: UI.TEXT.GREY, fontSize: 13, textDecoration: "underline" }}
            >
              Back to login
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Screen 2: MFA verification or enrollment
  if (mfa) {
    const isEnrolling = mfa.needsEnrollment;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          fontFamily: "Assistant, sans-serif",
          background: UI.BACKGROUND.STANDARD,
          padding: 20,
        }}
      >
        <style>{spinKeyframes}</style>
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            background: "#fff",
            padding: 32,
            borderRadius: 12,
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            textAlign: "center",
          }}
        >
          <h2 style={{ color: UI.TEXT.ACTIVE, marginBottom: 8, fontSize: 22 }}>
            {isEnrolling ? "Set up Two-Factor Authentication" : "Two-Factor Authentication"}
          </h2>
          <p style={{ color: UI.TEXT.GREY, marginBottom: 24, fontSize: 14 }}>
            {isEnrolling
              ? "Your organization requires MFA. Scan the QR code with your authenticator app, then enter the 6-digit code."
              : "Please enter the 6-digit verification code from your authenticator app."}
          </p>

          <MfaStep
            challenge={mfa}
            onSuccess={handleAuthSuccess}
            onBack={() => navigate("/login")}
          />
        </div>
      </div>
    );
  }

  // Screen 3: Error state (takes precedence over org picker to prevent retrying with a spent token)
  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: UI.BACKGROUND.STANDARD,
          padding: 24,
          textAlign: "center",
          fontFamily: "Assistant, sans-serif",
        }}
      >
        <style>{spinKeyframes}</style>
        <div
          style={{
            maxWidth: 420,
            background: "#fff",
            padding: 32,
            borderRadius: 12,
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          }}
        >
          <p
            style={{
              color: UI.TEXT.DANGER,
              marginBottom: 12,
              fontSize: 15,
              fontWeight: 500,
              lineHeight: 1.4,
            }}
          >
            {error}
          </p>
          {countdown !== null && (
            <p style={{ color: UI.TEXT.GREY, marginBottom: 20, fontSize: 13 }}>
              Redirecting to login in {countdown} {countdown === 1 ? "second" : "seconds"}…
            </p>
          )}
          <button
            onClick={() => navigate("/login", { replace: true })}
            style={{
              display: "inline-block",
              padding: "12px 24px",
              backgroundColor: UI.TEXT.ACTIVE,
              color: "#fff",
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  // Screen 4: Multiple organizations picker
  if (organizations && organizations.length > 1) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          fontFamily: "Assistant, sans-serif",
          background: UI.BACKGROUND.STANDARD,
          padding: 20,
        }}
      >
        <style>{spinKeyframes}</style>
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            background: "#fff",
            padding: 32,
            borderRadius: 12,
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          }}
        >
          <h2 style={{ color: UI.TEXT.ACTIVE, marginBottom: 8, fontSize: 20, textAlign: "center" }}>
            Select Organization
          </h2>
          <p style={{ color: UI.TEXT.GREY, marginBottom: 24, fontSize: 14, textAlign: "center" }}>
            We found multiple organizations linked to your email. Please select one:
          </p>

          <div style={{ width: "100%" }}>
            {organizations.map((org) => (
              <button
                key={org.organization_id}
                onClick={() => handleSelectOrg(org.organization_id)}
                disabled={!!loadingSelection}
                style={{
                  width: "100%",
                  minHeight: 48,
                  height: 48,
                  padding: "0 16px",
                  marginBottom: 10,
                  borderRadius: 8,
                  border: `1px solid ${UI.BORDER.STANDARD}`,
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: 15,
                  fontWeight: 500,
                  boxSizing: "border-box",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span>{org.organization_name}</span>
                  {org.requires_sso && (
                    <span
                      style={{
                        fontSize: 10,
                        backgroundColor: "#fce4ec",
                        color: "#c62828",
                        padding: "2px 6px",
                        borderRadius: 4,
                        marginLeft: 8,
                        fontWeight: 600,
                      }}
                    >
                      SSO
                    </span>
                  )}
                </div>
                {loadingSelection === org.organization_id ? (
                  <InlineSpinner />
                ) : (
                  <span style={{ color: UI.TEXT.GREY }}>→</span>
                )}
              </button>
            ))}
          </div>

          <div style={{ marginTop: 20, textAlign: "center" }}>
            <a
              href="/login"
              style={{ color: UI.TEXT.GREY, fontSize: 13, textDecoration: "underline" }}
            >
              Cancel and go back
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Screen 5: Loading state
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: UI.BACKGROUND.STANDARD,
        padding: 24,
        textAlign: "center",
        fontFamily: "Assistant, sans-serif",
      }}
    >
      <style>{spinKeyframes}</style>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Spinner />
        <p style={{ marginTop: 16, color: UI.TEXT.GREY, fontSize: 14 }}>Completing sign-in…</p>
      </div>
    </div>
  );
};

export default SSOCallback;
